import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient, type QueryClient, type UseQueryResult } from '@tanstack/react-query';
import type {
  AssistantEditBody, AssistantObjectRef, AssistantPendingDTO, AssistantMeDTO,
} from '@contracts/assistant';
import type {
  AppOpenBody, AppOpenBubbleDTO, AppOpenDTO, BriefingCurrentDTO, BriefingDTO, BriefingListItemDTO, NudgeActionResultDTO,
  NudgeDismissDTO, NudgeSnoozeUntil, NudgeTrayDTO, ProactivePrefsBody, ProactivePrefsDTO, ProactiveRule, WatchDTO,
  WatchPatchBody,
} from '@contracts/assistant-proactive';
import type { Id } from '@contracts/common';
import { api, ApiClientError } from '../../../api/client';
import { isAssistantOff } from '../api';

/**
 * The personal side of "Hana chủ động" (S3 spec §12.2, D9, D22, D23): the person's own prefs and the app-open ping.
 * The key is exported so the admin tab, whose save changes the company defaults this page shows ("Theo công ty (…)"),
 * can refresh it.
 */

export const PROACTIVE_PREFS_KEY = ['assistant', 'proactive-prefs'] as const;
const PREFS_PATH = '/api/assistant/proactive/prefs';

/** `ASSISTANT_DISABLED` is a settled answer the page shows (the server's own words), never retried. */
export { isAssistantOff };

/** `enabled` false: the read waits (the wizard reads the prefs only on its step 4). */
export function useProactivePrefs(enabled = true): UseQueryResult<ProactivePrefsDTO, ApiClientError> {
  return useQuery<ProactivePrefsDTO, ApiClientError>({
    queryKey: PROACTIVE_PREFS_KEY,
    queryFn: () => api<ProactivePrefsDTO>(PREFS_PATH),
    enabled,
    retry: (count, error) => !isAssistantOff(error) && count < 2,
  });
}

export const saveProactivePrefs = (body: ProactivePrefsBody, requestId: Id) =>
  api<ProactivePrefsDTO>(PREFS_PATH, { method: 'PUT', body, requestId });

/** What the shell's one app-open answered: `settled` once it has answered either way (L3 D124), `bubble` the opening
 *  bubble the server granted (D122, D123; null when none, or on a failure). */
export type AppOpenState = { settled: boolean; bubble: AppOpenBubbleDTO | null };

/**
 * D22: one `POST /api/assistant/app-open` per shell mount, once the assistant prefs settle as usable (non-null) — never
 * for a person with the assistant off. A failure is nobody's business on screen. L3 D122: `greet()` is read when the
 * call goes, so the bubble is asked for only where it may show; a granted answer refreshes the card's briefing (the
 * open may just have composed it).
 */
export function useAppOpen(prefs: AssistantMeDTO | null | undefined, greet: () => boolean): AppOpenState {
  const sent = useRef(false);
  const queryClient = useQueryClient();
  const greetNow = useRef(greet);
  useEffect(() => { greetNow.current = greet; });
  const [state, setState] = useState<AppOpenState>({ settled: false, bubble: null });
  const usable = prefs !== null && prefs !== undefined;
  useEffect(() => {
    if (!usable || sent.current) return;
    sent.current = true;
    const body: AppOpenBody = { greet: greetNow.current() };
    api<AppOpenDTO>('/api/assistant/app-open', { method: 'POST', body })
      .then((answer) => {
        void queryClient.invalidateQueries({ queryKey: BRIEFING_CURRENT_KEY });
        setState({ settled: true, bubble: answer?.bubble ?? null });
      })
      .catch(() => setState({ settled: true, bubble: null }));
  }, [usable, queryClient]);
  return state;
}

// The nudge tray (S3 L2 §7.3–§7.5, D59, D60, D76).

/** One query for the badge and the tray (D76). Under `['assistant']`, so an undo's refresh reads the tray again. */
export const NUDGES_KEY = ['assistant', 'nudges'] as const;
export const NUDGES_PATH = '/api/assistant/nudges';
/** D76: the tray is read again every minute while the tab is on screen. */
export const NUDGE_POLL_MS = 60_000;
const nudgePath = (id: Id) => `${NUDGES_PATH}/${encodeURIComponent(id)}`;

/** A 4xx is a settled answer (the assistant off for this person, no route): read again only when asked. */
const isSettledRefusal = (error: unknown): boolean =>
  error instanceof ApiClientError && error.status >= 400 && error.status < 500;

/** S7 L2 T19: while a run resumes after its card was confirmed, the tray is read every few seconds, for two minutes. */
export const RUN_RESUME_POLL_MS = 3_000;
export const RUN_RESUME_WATCH_MS = 120_000;
let runConfirmedAt = 0;
/** Called once a run's card is confirmed: the run resumes in the background and its summary should show soon. */
export const watchRunResume = (at: number = Date.now()): void => { runConfirmedAt = at; };

/**
 * D216: after "Chạy thử ngay" the tray is read every 5 s until that run's card reaches it (a queued or running order run
 * has none yet), for at most 5 minutes; a run that ends with nothing to show is read for the 5 minutes, then D76 again.
 */
export const ORDER_RUN_POLL_MS = 5_000;
export const ORDER_RUN_WATCH_MS = 300_000;
export type OrderRunWatch = { runId: Id; at: number };
let orderRun: OrderRunWatch | null = null;
/** Called once "Chạy thử ngay" queued a run (`null` stops the watch). */
export const watchOrderRun = (runId: Id | null, at: number = Date.now()): void => {
  orderRun = runId === null ? null : { runId, at };
};
/** The run "Chạy thử ngay" last queued, while it is watched. */
export const orderRunWatched = (): OrderRunWatch | null => orderRun;

/**
 * How soon to read the tray again: sooner while a run confirmed in the last two minutes is still resuming — waiting
 * with nothing left to confirm, or queued or running again (D97: the server keeps a resuming run's card) — or while
 * the run "Chạy thử ngay" queued in the last 5 minutes has no settled card in the tray yet (D216).
 */
export function nudgePollMs(data: NudgeTrayDTO | undefined, confirmedAt: number, now: number,
  watched: OrderRunWatch | null = null): number {
  const resuming = data?.runCards?.some((c) => c.status === 'queued' || c.status === 'running'
    || (c.status === 'waiting' && c.pending.length === 0)) ?? false;
  if (resuming && now - confirmedAt <= RUN_RESUME_WATCH_MS) return RUN_RESUME_POLL_MS;
  const arrived = data?.runCards?.some((c) => c.runId === watched?.runId && c.status !== 'queued'
    && c.status !== 'running') ?? false;
  return watched !== null && !arrived && now - watched.at <= ORDER_RUN_WATCH_MS ? ORDER_RUN_POLL_MS : NUDGE_POLL_MS;
}

export function useNudges(enabled: boolean): UseQueryResult<NudgeTrayDTO, ApiClientError> {
  return useQuery<NudgeTrayDTO, ApiClientError>({
    queryKey: NUDGES_KEY,
    queryFn: () => api<NudgeTrayDTO>(NUDGES_PATH),
    enabled,
    staleTime: 30_000,
    retry: (count, error) => !isSettledRefusal(error) && count < 2,
    // D76: every minute while the tab is visible (sooner while a confirmed run resumes, D216 after "Chạy thử ngay"); react-query also skips a
    // tick while the tab is hidden (`refetchIntervalInBackground` stays false).
    // C-F1: never the tab's state here, or a read that lands on a hidden tab stops the poll for good.
    refetchInterval: (query) => (isSettledRefusal(query.state.error)
      ? false : nudgePollMs(query.state.data, runConfirmedAt, Date.now(), orderRun)),
    refetchIntervalInBackground: false,
  });
}

export const snoozeNudge = (id: Id, until: NudgeSnoozeUntil, requestId: Id) =>
  api<unknown>(`${nudgePath(id)}/snooze`, { method: 'POST', body: { until }, requestId });
export const dismissNudge = (id: Id, requestId: Id) =>
  api<NudgeDismissDTO>(`${nudgePath(id)}/dismiss`, { method: 'POST', body: {}, requestId });
export const doneNudge = (id: Id, requestId: Id) =>
  api<unknown>(`${nudgePath(id)}/done`, { method: 'POST', body: {}, requestId });
/** §7.5: the key travels in the body and the client keeps it on every retry of one click. */
export const actNudge = (id: Id, index: number, requestId: Id) =>
  api<NudgeActionResultDTO>(`${nudgePath(id)}/actions/${index}`, { method: 'POST', body: { requestId } });

// A background run's card in the tray (S7 §9.3, §8.8): its open cards, its undo and its "⋯ Ẩn", on the run's own routes.

const runPath = (id: Id) => `/api/assistant/runs/${encodeURIComponent(id)}`;
const runActionPath = (runId: Id, actionId: Id) => `${runPath(runId)}/actions/${encodeURIComponent(actionId)}`;

/** What a run card's Xác nhận answers: the objects it touched (an outside call answers its state alone). */
export type RunActDone = { actionId: Id; objects?: AssistantObjectRef[] };

export const actRunCard = (runId: Id, actionId: Id, kind: 'confirm' | 'reject', requestId: Id) =>
  api<RunActDone>(`${runActionPath(runId, actionId)}/${kind}`, { method: 'POST', body: {}, requestId });
export const editRunCard = (runId: Id, actionId: Id, body: AssistantEditBody, requestId: Id) =>
  api<AssistantPendingDTO>(`${runActionPath(runId, actionId)}/edit`, { method: 'POST', body, requestId });
export const undoRun = (runId: Id, requestId: Id) =>
  api<unknown>(`${runPath(runId)}/undo`, { method: 'POST', body: {}, requestId });
export const dismissRun = (runId: Id, requestId: Id) =>
  api<{ runId: Id; dismissedAt: string }>(`${runPath(runId)}/dismiss`, { method: 'POST', body: {}, requestId });

/** The whole PUT body of the prefs as read: the memory page (L4 D166) and "Tắt nhắc này" (D60) change one part of it. */
export const bodyOf = (p: ProactivePrefsDTO, disabledRules: ProactiveRule[]): ProactivePrefsBody => ({
  version: p.version, nudgesEnabled: p.nudgesEnabled, briefingDaily: p.briefingDaily, briefingWeekly: p.briefingWeekly,
  briefingTime: p.briefingTime, briefingSections: p.briefingSections, quiet: p.quiet, dailyCap: p.dailyCap,
  disabledRules,
});

/**
 * "Tắt nhắc này" (D60): the person's prefs read fresh, the rule added to `disabledRules` at their version. A 409 (another
 * save came first) reads them again and tries once more. Answers the saved prefs.
 */
export async function disableRule(rule: ProactiveRule): Promise<ProactivePrefsDTO> {
  for (let attempt = 0; ; attempt += 1) {
    const prefs = await api<ProactivePrefsDTO>(PREFS_PATH);
    if (prefs.disabledRules.includes(rule)) return prefs;
    try {
      return await saveProactivePrefs(bodyOf(prefs, [...prefs.disabledRules, rule]), crypto.randomUUID());
    } catch (e) {
      if (attempt > 0 || !(e instanceof ApiClientError) || e.status !== 409) throw e;
    }
  }
}

// The person's own reminders and watches (§12.2, L2 D66, D74, D91).

export const WATCHES_KEY = ['assistant', 'watches'] as const;
const WATCHES_PATH = '/api/assistant/watches';
const watchPath = (id: Id) => `${WATCHES_PATH}/${encodeURIComponent(id)}`;

/** The active rows only (D91). Readable with Hana off or read-only, so it never waits on the prefs. */
export function useWatches(): UseQueryResult<WatchDTO[], ApiClientError> {
  return useQuery<WatchDTO[], ApiClientError>({
    queryKey: WATCHES_KEY,
    queryFn: async () => (await api<{ items: WatchDTO[] }>(WATCHES_PATH)).items,
    retry: (count, error) => !isSettledRefusal(error) && count < 2,
  });
}

export const editReminder = (id: Id, body: WatchPatchBody, requestId: Id) =>
  api<WatchDTO>(watchPath(id), { method: 'PATCH', body, requestId });
/** Turning a row off; one already off answers 200 as it is (D91). */
export const cancelWatch = (id: Id, requestId: Id) =>
  api<WatchDTO>(watchPath(id), { method: 'DELETE', body: {}, requestId });

// The briefing card (S3 L3 §8.4, D124–D126).

export const BRIEFING_CURRENT_KEY = ['assistant', 'briefing', 'current'] as const;
/** The rhythm tab's briefing card: the card's "Cài đặt briefing" opens `/assistant/me?tab=nhip#ban-tom-tat` (D126, S8 D85). */
export const BRIEFING_SECTION_ID = 'ban-tom-tat';
const BRIEFINGS_PATH = '/api/assistant/briefings';
const briefingPath = (id: Id) => `${BRIEFINGS_PATH}/${encodeURIComponent(id)}`;

/**
 * D135: while the briefing on the card (or its alternate) shows the template and waits for the worker's model text
 * (`textPending`: app-open's 8 s, or a "Soạn lại" past its deadline), the card reads every 5 s, for at most 3 minutes
 * per pending briefing (`id:composedAt`); then every minute again (D124).
 */
export const BRIEFING_PENDING_POLL_MS = 5_000;
export const BRIEFING_PENDING_WATCH_MS = 180_000;
export type BriefingPendingWatch = { key: string; at: number };

/** The pending briefing (or alternate) by `id:composedAt`, or null with nothing pending (an old server sends none). */
export function pendingKeyOf(data: BriefingCurrentDTO | undefined): string | null {
  const shown = [data?.briefing, data?.briefing?.alternate].filter((b): b is BriefingDTO => b != null);
  const keys = shown.filter((b) => b.textPending === true).map((b) => `${b.id}:${b.composedAt}`);
  return keys.length === 0 ? null : keys.join('|');
}

/** How soon to read the card again: every 5 s while the briefing first seen pending at `watch.at` still is, for 3 minutes. */
export function briefingPollMs(data: BriefingCurrentDTO | undefined, watch: BriefingPendingWatch | null,
  now: number): number {
  const key = pendingKeyOf(data);
  return key !== null && watch?.key === key && now - watch.at <= BRIEFING_PENDING_WATCH_MS
    ? BRIEFING_PENDING_POLL_MS : NUDGE_POLL_MS;
}

/** One watch per query (a new client starts afresh); a new pending key restarts it, nothing pending clears it. */
const pendingWatches = new WeakMap<object, BriefingPendingWatch>();
function watchPending(query: object, data: BriefingCurrentDTO | undefined, now: number): BriefingPendingWatch | null {
  const key = pendingKeyOf(data);
  if (key === null) { pendingWatches.delete(query); return null; }
  const was = pendingWatches.get(query);
  if (was?.key === key) return was;
  const next = { key, at: now };
  pendingWatches.set(query, next);
  return next;
}

/**
 * D124: the card's briefing, read once app-open has settled (it may have just composed it) and then every minute while
 * the tab is on screen (sooner while its model text is pending, D135). A 4xx (the assistant off) is settled: no card,
 * no further reads until asked.
 */
export function useBriefingCurrent(enabled: boolean): UseQueryResult<BriefingCurrentDTO, ApiClientError> {
  return useQuery<BriefingCurrentDTO, ApiClientError>({
    queryKey: BRIEFING_CURRENT_KEY,
    queryFn: () => api<BriefingCurrentDTO>(`${BRIEFINGS_PATH}/current`),
    enabled,
    staleTime: 30_000,
    retry: (count, error) => !isSettledRefusal(error) && count < 2,
    refetchInterval: (query) => {   // C-F1, as above
      if (isSettledRefusal(query.state.error)) return false;
      const now = Date.now();
      return briefingPollMs(query.state.data, watchPending(query, query.state.data, now), now);
    },
    refetchIntervalInBackground: false,
  });
}

/** The cached answer with `next` in place of the briefing (or the weekly's alternate) it replaces. */
export function replaceBriefing(qc: QueryClient, next: BriefingDTO): void {
  qc.setQueryData<BriefingCurrentDTO>(BRIEFING_CURRENT_KEY, (was) => {
    const main = was?.briefing;
    if (main == null) return was;
    if (main.id === next.id) return { briefing: { ...next, alternate: main.alternate ?? null } };
    if (main.alternate?.id === next.id) return { briefing: { ...main, alternate: { ...next, alternate: null } } };
    return was;
  });
}

/** D126 "Đã xem": owner only, idempotent; answers the projected briefing with its `seenAt`. */
export const markBriefingSeen = (id: Id, requestId: Id) =>
  api<BriefingDTO>(`${briefingPath(id)}/seen`, { method: 'POST', body: {}, requestId });

/** D125 "Soạn lại": 409 when the card is no longer current, 429 past three presses a day (the server's words). */
export const recomposeBriefing = (id: Id, requestId: Id) =>
  api<BriefingDTO>(`${briefingPath(id)}/recompose`, { method: 'POST', body: {}, requestId });

// "Briefing trước" (S3 L3 §8.6, D127).

/** The person's last 30 briefings, metadata only. Read afresh each time the list opens. */
export function useBriefingList(enabled: boolean): UseQueryResult<BriefingListItemDTO[], ApiClientError> {
  return useQuery<BriefingListItemDTO[], ApiClientError>({
    queryKey: ['assistant', 'briefing', 'list'],
    queryFn: () => api<BriefingListItemDTO[]>(`${BRIEFINGS_PATH}?limit=30`),
    enabled,
    staleTime: 0,
    refetchOnMount: 'always',
    retry: (count, error) => !isSettledRefusal(error) && count < 2,
  });
}

/** One of the person's briefings, read-only and re-projected for them now (hidden = missing: a 404 is final). */
export function useBriefing(id: Id | null): UseQueryResult<BriefingDTO, ApiClientError> {
  return useQuery<BriefingDTO, ApiClientError>({
    queryKey: ['assistant', 'briefing', 'detail', id],
    queryFn: () => api<BriefingDTO>(briefingPath(id!)),
    enabled: id !== null,
    staleTime: 0,
    refetchOnMount: 'always',
    retry: (count, error) => !isSettledRefusal(error) && count < 2,
  });
}

/** D127: the panel offers the history to a person who receives either kind (the company's switches and their own). */
export const receivesBriefings = (p: ProactivePrefsDTO): boolean =>
  p.company.proactiveEnabled && p.company.briefingsEnabled && (p.briefingDaily || p.briefingWeekly);
